<script setup lang="ts">
import { ref, watch } from 'vue'
import { Moon, Sun, Settings } from 'lucide-vue-next'
import { UserButton } from '@clerk/vue'
import { useRouter } from 'vue-router'
import { useConfigStore } from '@/stores/config'

const configStore = useConfigStore()
const router = useRouter()

const stored = localStorage.getItem('color-scheme')
const isDark = ref(
  stored !== null ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches,
)

watch(
  isDark,
  (val) => {
    document.documentElement.classList.toggle('dark', val)
    localStorage.setItem('color-scheme', val ? 'dark' : 'light')
  },
  { immediate: true },
)
</script>

<template>
  <header class="flex h-14 shrink-0 items-center justify-between border-b px-4 z-10 bg-background">
    <div class="flex items-center gap-2 px-2">
      <img
        :src="configStore.branding.logoPath"
        :alt="configStore.branding.appName"
        class="h-6 w-auto"
      />
      <span class="text-lg font-semibold tracking-tight">{{ configStore.branding.appName }}</span>
    </div>

    <div class="flex items-center gap-3">
      <button
        aria-label="Settings"
        class="md:hidden flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        @click="router.push({ name: 'settings' })"
      >
        <Settings class="size-4" />
      </button>

      <button
        role="switch"
        :aria-checked="isDark"
        :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        class="relative inline-flex h-5 w-10 shrink-0 inset-shadow cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        :class="isDark ? 'bg-primary' : 'bg-input'"
        @click="isDark = !isDark"
      >
        <Moon class="absolute left-1 size-3 text-muted-background invert" />
        <Sun class="absolute right-1 size-3 text-muted-foreground" />
        <span
          class="pointer-events-none block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform border"
          :class="isDark ? 'translate-x-5' : 'translate-x-0'"
        />
      </button>

      <div class="scale-120 origin-top ml-2">
        <UserButton />
      </div>
    </div>
  </header>
</template>
